import { z } from 'zod';
import { isCalendarDate } from '../campaigns.js';
import { CONTENT_LIMITS, CONTENT_PRIORITIES, CONTENT_SEASONS } from '../content/content.js';
import { ENABLED_VARIANT_FORMATS } from '../content/formats.js';
import { occasionRefSchema } from '../director/occasions.js';
/**
 * `topic_planner` (spec 80 §4.7, S80-3): topic proposals for a period of at
 * most four weeks. Each proposal becomes a content item in status `idea` with
 * origin `ai` and a first version authored by the run; the editor decides
 * what happens with it (AI never approves or publishes).
 */
export const TOPIC_PLANNER_LIMITS = {
    /** `period_end` at most 27 days after `period_start`: four weeks including both days (AC-80-021). */
    maxPeriodDays: 28,
    minCount: 1,
    maxCount: 30,
    maxPostsPerWeek: 21,
    /** Reels per week a run may plan (E400). */
    maxReelsPerWeek: 7,
    /** Recent publications and actual pillar shares look back this many weeks. */
    historyWeeks: 8,
    maxRationaleLength: 1000,
    maxRecentPublications: 200,
    maxPlannedItems: 200,
    /** Focus scope of a run (S110-5, optional): pillar and apartment IDs one run may name, and the length of a theme passed in. */
    maxScopePillars: 20,
    maxScopeApartments: 50,
    /** Focus entries of a run (E470) and entries one proposal may name. */
    maxScopeEntries: 50,
    maxProposalEntries: 5,
    /** Due entries the context carries at most (prompt v9 on, E470). */
    maxDueEntries: 50,
    maxThemeLength: 200,
    /** Performance context (prompt v6 on, S160-4, REQ-AUT-009): look-back days, posts with metrics it needs at least, posts listed per side. */
    performanceWindowDays: 90,
    performanceMinPosts: 4,
    performancePostsPerSide: 5,
    /** A post counts as strong or weak at this many percent above or below the mean. */
    performanceMarginPercent: 25,
    /** No topic twice within this many days before or after (prompt v7 on, E300 S300-2, REQ-110-024). */
    recentTopicDays: 30,
    maxRecentTopics: 60,
};
/** A title as the 30-day topic rule compares it: lower-case, trimmed, inner whitespace collapsed (spec 110 section 4.12). */
export function normalizeTopicTitle(title) {
    return title.trim().replace(/\s+/g, ' ').toLocaleLowerCase('de');
}
const calendarDate = z.string().refine(isCalendarDate, { message: 'expected a calendar day YYYY-MM-DD' });
/** Whole days from `start` to `end` (both `YYYY-MM-DD`); negative when `end` is earlier. */
export function calendarDaysBetween(start, end) {
    return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000);
}
/** The period rule of AC-80-021, as field errors: `period_end` not before `period_start` and at most 27 days after it. */
export function topicPeriodErrors(start, end) {
    const days = calendarDaysBetween(start, end);
    if (days < 0)
        return [{ field: 'period_end', code: 'before_start' }];
    if (days > TOPIC_PLANNER_LIMITS.maxPeriodDays - 1)
        return [{ field: 'period_end', code: 'period_too_long' }];
    return [];
}
/** The day `days` after `date` (`YYYY-MM-DD`). */
export function addCalendarDays(date, days) {
    return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}
/** Meteorological season of a calendar day (northern hemisphere, the Alps). */
export function seasonOfDate(date) {
    const month = Number(date.slice(5, 7));
    if (month >= 3 && month <= 5)
        return 'spring';
    if (month >= 6 && month <= 8)
        return 'summer';
    if (month >= 9 && month <= 11)
        return 'autumn';
    return 'winter';
}
/** The distinct seasons a period touches, in calendar order. */
export function seasonsOfPeriod(start, end) {
    const seasons = [];
    for (let day = start; day <= end; day = addCalendarDays(day, 1)) {
        const season = seasonOfDate(day);
        if (!seasons.includes(season))
            seasons.push(season);
    }
    return seasons;
}
/** A list of distinct IDs of at most `max` entries (a scope list of the topic planner). */
const distinctIds = (max) => z
    .array(z.uuid())
    .max(max)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate IDs' });
/**
 * The run's parameters. `pillar_ids`, `apartment_ids`, `theme_slot_id` and
 * `theme` are the optional focus scope (S110-5, T110-5-10): the caller (e.g.
 * module 110's inbox) names pillars and apartments of the property and passes
 * a theme as plain text; module 80 reads no theme slot. The context builder
 * keeps only the IDs that are active pillars or listed apartments of the run's
 * property in the run's tenant, so an unknown or foreign ID is ignored and never
 * reaches a provider. `theme_slot_id` is provenance only (stored with the
 * params, never sent) and needs its `theme`; so is `occasion`, which module
 * 110 copies into the idea's `topic_proposal` message.
 */
export const topicPlannerInputSchema = z
    .object({
    property_id: z.uuid(),
    period_start: calendarDate,
    period_end: calendarDate,
    count: z.int().min(TOPIC_PLANNER_LIMITS.minCount).max(TOPIC_PLANNER_LIMITS.maxCount),
    /** The posting rhythm the dates follow (AC-80-027, `target_posts_per_week`); absent: derived from `count` and the weeks of the period. */
    posts_per_week: z.int().min(1).max(TOPIC_PLANNER_LIMITS.maxPostsPerWeek).optional(),
    /** The reel share (E400, `reels_per_week`): ideas per ISO week created as reels (`INBOX_REEL_FORMATS`); absent or 0: none. */
    reels_per_week: z.int().min(0).max(TOPIC_PLANNER_LIMITS.maxReelsPerWeek).optional(),
    /** Focus campaign: proposals should serve it where it fits. */
    campaign_id: z.uuid().nullable(),
    /** Focus pillars, most wanted first. */
    pillar_ids: distinctIds(TOPIC_PLANNER_LIMITS.maxScopePillars).optional(),
    /** Focus apartments (profiles with apartments only; ignored otherwise). */
    apartment_ids: distinctIds(TOPIC_PLANNER_LIMITS.maxScopeApartments).optional(),
    /** Focus entries (E470): any entries of the property; `apartment_ids` join them from prompt v9 on. */
    entry_ids: distinctIds(TOPIC_PLANNER_LIMITS.maxScopeEntries).optional(),
    /** The caller's theme slot the theme comes from; provenance only. */
    theme_slot_id: z.uuid().optional(),
    /** A theme a human planned for the period (content, not a verified fact). */
    theme: z.string().trim().min(1).max(TOPIC_PLANNER_LIMITS.maxThemeLength).optional(),
    /** The occasion the theme describes (spec 110 section 4.8, S160-3); provenance only, never sent. */
    occasion: occasionRefSchema.optional(),
})
    .refine((input) => topicPeriodErrors(input.period_start, input.period_end).length === 0, { path: ['period_end'], message: 'period too long or reversed' })
    .refine((input) => input.theme_slot_id === undefined || input.theme !== undefined, { path: ['theme'], message: 'a theme slot needs its theme' })
    .refine((input) => input.occasion === undefined || input.theme !== undefined, { path: ['theme'], message: 'an occasion needs its theme' });
export const topicProposalSchema = z.object({
    title: z.string().trim().min(1).max(CONTENT_LIMITS.titleMaxLength),
    description: z.string().max(CONTENT_LIMITS.descriptionMaxLength),
    /** One of the context's pillar keys. */
    pillar_key: z.string().min(1).max(100),
    /** One of the context's campaign IDs, or `null`. */
    campaign_id: z.string().nullable(),
    evergreen: z.boolean(),
    /**
     * `null` when the context's period lists no `seasons` (prompt v3, a profile
     * without seasons, S40-G10); module 40 drops a season for such a profile
     * whatever prompt version ran.
     */
    season: z.enum(CONTENT_SEASONS).nullable(),
    /** A day inside the requested period. */
    desired_date: calendarDate,
    priority: z.enum(CONTENT_PRIORITIES),
    formats: z.array(z.enum(ENABLED_VARIANT_FORMATS)).min(1).max(ENABLED_VARIANT_FORMATS.length),
    /** One of the context's apartment IDs, or `null` (prompts v1-v8); the idea is then about that entry (E470). */
    apartment_id: z.string().nullable(),
    /**
     * Entries the topic is about (prompt v9 on, E470), each from the context's `due_entries` or `focus.entries`; the
     * idea is created with them. A default, not optional: the provider schema needs every property.
     */
    entry_ids: z.array(z.string()).max(TOPIC_PLANNER_LIMITS.maxProposalEntries).default([]),
    rationale: z.string().min(1).max(TOPIC_PLANNER_LIMITS.maxRationaleLength),
});
export const topicPlannerOutputSchema = z.object({
    proposals: z.array(topicProposalSchema).max(TOPIC_PLANNER_LIMITS.maxCount),
});
//# sourceMappingURL=topic-planner.js.map